import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ManagementServiceService } from 'src/app/management-service.service';
import { B2bComponent } from '../b2b.component';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';

import * as _moment from 'moment';
const moment = _moment;

export const MY_FORMATS = {
  parse: {
    dateInput: 'LL',
  },
  display: {
    dateInput: 'YYYY-MM-DD',
    monthYearLabel: 'YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'YYYY',
  },
};

@Component({
  selector: 'app-requested-individual-items',
  templateUrl: './requested-individual-items.component.html',
  styleUrls: ['./requested-individual-items.component.css'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
})
export class RequestedIndividualItemsComponent implements OnInit {

  constructor(
    public parent: B2bComponent,
    private managementService: ManagementServiceService,
    public translate: TranslateService
  ) {
    translate.addLangs(['en', 'ro', 'de', 'es', 'fr', 'it', 'pl', 'pt']);
    translate.setDefaultLang('en');
    const browserLang = this.parent.getLanguage();
    translate.use(browserLang.match(/en|ro|de|es|fr|it|pl|pt/) ? browserLang : 'en');

    // Debounce main search
    this.searchTermUpdate.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe(value => {
      this.Filter.SearchTerm = value;
      this.LoadItems();
    });
  }

  // State
  Items: any[] = [];
  isLoading: boolean = false;
  errorMessage: string = '';

  // Filter
  Filter: any = {};
  FromDate: FormControl = new FormControl();
  ToDate: FormControl = new FormControl();
  MasterData: any = {};

  // Search
  searchTermUpdate = new Subject<string>();

  // Dropdown states
  showStatusDropdown: boolean = false;
  showPeriodDropdown: boolean = false;

  ngOnInit(): void {
    this.InitializeFilter();
    this.GetMasterData();
  }

  InitializeFilter() {
    this.Filter = {};
    this.Filter.SearchTerm = '';

    // Default to last 6 months
    this.Filter.FromDate = moment().subtract(6, 'months').format('YYYY-MM-DD');
    this.Filter.ToDate = moment().format('YYYY-MM-DD');
    this.FromDate = new FormControl(moment().subtract(6, 'months'));
    this.ToDate = new FormControl(moment());

    this.Filter.Pager = {};
    this.Filter.Pager.PageIndex = 1;
    this.Filter.Pager.PageSize = 100; // Fixed page size for display-only

    this.Filter.Partner = this.parent.B2BPartner; // B2B partner context
    this.Filter.IndividualStatus = null;
    this.Filter.Period = null;
  }

  GetMasterData() {
    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
    };

    this.managementService.GetOfferRequestMasterData(request).subscribe((response: any) => {
      if (response?.Status === 1) {
        this.MasterData = response;
        this.LoadItems();
      }
    });
  }

  LoadItems() {
    this.isLoading = true;
    this.errorMessage = '';

    // Build request
    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      Filter: { ...this.Filter }
    };

    // Format dates
    if (this.FromDate?.value?._d) {
      request.Filter.FromDate = moment(this.FromDate.value._d).format('YYYY-MM-DD');
    }
    if (this.ToDate?.value?._d) {
      request.Filter.ToDate = moment(this.ToDate.value._d).format('YYYY-MM-DD');
    }

    // Ensure partner is set for B2B context
    request.Filter.Partner = this.parent.B2BPartner;

    this.managementService.GetOfferRequestsIndividualItemsFiltered(request).subscribe(
      (response: any) => {
        this.isLoading = false;
        if (response?.Status !== 1) {
          this.errorMessage = response?.Message || this.translate.instant('GenericError');
          this.Items = [];
        } else {
          const items = response?.Items || [];
          this.Items = items.map((cartItem: any, index: number) => {
            const leadTime = cartItem?.SelectedOffer?.LeadTime;
            const etaDisplay = leadTime?.Units > 0 ? `${leadTime.Units} ${leadTime.Type?.Name ?? ''}` : '------';

            return {
              index: index + 1,
              brand: cartItem?.Product?.Brand?.Name || cartItem?.UserSelectedBrand?.Name || '',
              partNumber: cartItem?.Product?.PartNumber || '',
              name: cartItem?.UIProductValue || cartItem?.Product?.Name || '',
              quantity: cartItem?.Quantity || 0,
              price: cartItem?.SelectedOffer?.SellingPrice || cartItem?.Price?.UIUnitPrice || '',
              currency: cartItem?.SelectedOffer?.Currency?.Symbol || cartItem?.Price?.Currency?.Symbol || '',
              total: cartItem?.Price?.UIQtyPrice || '',
              eta: etaDisplay,
              supplier: cartItem?.SelectedOffer?.Partner?.Name || '',
              status: cartItem?.RequestStatus?.Name || '',
              statusID: cartItem?.RequestStatus?.ID || 0,
              ReferenceNumber: cartItem?.CartItemOrderInfo?.ReferenceNumber || '',
              CreatedDate: cartItem?.CartItemOrderInfo?.OrderEventRegister || '',
              CartItem: cartItem
            };
          });
        }
      },
      (error: any) => {
        console.error('Error loading items:', error);
        this.isLoading = false;
        this.errorMessage = this.translate.instant('GenericError');
        this.Items = [];
      }
    );
  }

  applyFilter() {
    this.LoadItems();
  }

  resetFilter() {
    this.InitializeFilter();
    this.LoadItems();
  }

  // Status dropdown
  onStatusDropdownFocus() {
    this.showStatusDropdown = !this.showStatusDropdown;
  }

  selectStatusFilter(status: any) {
    this.Filter.IndividualStatus = status;
    this.showStatusDropdown = false;
    this.applyFilter();
  }

  clearStatusFilter() {
    this.Filter.IndividualStatus = null;
    this.applyFilter();
  }

  // Period handling
  onPeriodDropdownFocus() {
    this.showPeriodDropdown = !this.showPeriodDropdown;
  }

  selectPeriod(period: any) {
    this.Filter.Period = period;
    this.Filter.FromDate = null;
    this.Filter.ToDate = null;
    this.showPeriodDropdown = false;
    this.applyFilter();
  }

  clearPeriodFilter() {
    this.Filter.Period = null;
    this.applyFilter();
  }

  getPeriodLabel(): string {
    return this.Filter.Period?.Name || '';
  }

  ClearPeriod() {
    this.Filter.Period = null;
  }

  // Format date for display
  formatDate(dateString: string): string {
    if (!dateString) return '';
    return moment(dateString).format('YYYY-MM-DD HH:mm');
  }
}
